"use client";

import {
	RiCalendarEventLine,
	RiMicLine,
	RiQuestionMark,
} from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import Image from "next/image";
import { memo, useCallback, useState } from "react";

export interface ArtistPhotoProps {
	name: string;
	imageUrl?: string | null;
	size?: "sm" | "md" | "lg" | "xl";
	priority?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	loading?: "eager" | "lazy";
	className?: string;
	rounded?: "circle" | "card";
	aspectRatio?: "square" | "wide";
	isSurprise?: boolean;
	isProgram?: boolean; // New prop for program sets
	objectPosition?:
		| "center"
		| "top"
		| "bottom"
		| "left"
		| "right"
		| "center top"
		| "center bottom";
}

const sizeClasses = {
	sm: { square: "w-8 h-8", wide: "w-12 h-8" },
	md: { square: "w-12 h-12", wide: "w-18 h-12" },
	lg: { square: "w-16 h-16", wide: "w-24 h-16" },
	xl: { square: "w-24 h-24", wide: "w-36 h-24" },
};

const iconSizes = {
	sm: 16,
	md: 24,
	lg: 24,
	xl: 32,
};

export const ArtistPhoto = memo<ArtistPhotoProps>(
	({
		name,
		imageUrl,
		size = "md",
		priority = false,
		fetchPriority = "auto",
		loading = "lazy",
		className = "",
		rounded = "circle",
		aspectRatio = "square",
		isSurprise = false,
		isProgram = false,
		objectPosition = "center",
	}) => {
		const [imageError, setImageError] = useState(false);
		const [imageLoaded, setImageLoaded] = useState(false);

		const handleImageError = useCallback(() => {
			setImageError(true);
		}, []);

		const handleImageLoad = useCallback(() => {
			setImageLoaded(true);
		}, []);

		const sizeClass = sizeClasses[size][aspectRatio];
		const iconSize = iconSizes[size];
		const roundedClass = rounded === "circle" ? "rounded-full" : "rounded-lg";

		return (
			<div
				className={cn(
					"relative overflow-hidden bg-gradient-to-b from-muted/60 to-muted",
					roundedClass,
					sizeClass,
					className,
				)}
			>
				{imageUrl && !imageError ? (
					<>
						<Image
							src={imageUrl}
							alt={`${name} photo`}
							fill
							sizes={
								className?.includes("w-full")
									? "(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
									: aspectRatio === "wide"
										? `${iconSize * 3}px`
										: `${iconSize * 2}px`
							}
							className={cn(
								"object-cover transition-opacity duration-200",
								imageLoaded ? "opacity-100" : "opacity-0",
							)}
							style={{ objectPosition }}
							priority={priority}
							fetchPriority={fetchPriority}
							{...(priority ? {} : { loading })}
							onError={handleImageError}
							onLoad={handleImageLoad}
						/>

						{/* Loading placeholder */}
						{!imageLoaded && (
							<div className="absolute inset-0 animate-pulse bg-muted" />
						)}
					</>
				) : (
					/* Fallback icon */
					<div className="flex h-full w-full items-center justify-center">
						{isProgram ? (
							<RiCalendarEventLine
								size={iconSize}
								className="text-muted-foreground"
							/>
						) : isSurprise ? (
							<RiQuestionMark
								size={iconSize}
								className="text-muted-foreground"
							/>
						) : (
							<RiMicLine size={iconSize} className="text-muted-foreground" />
						)}
					</div>
				)}
			</div>
		);
	},
);

ArtistPhoto.displayName = "ArtistPhoto";
